import React, { lazy, Suspense, useEffect } from 'react';
import { Redirect, Route, Switch, useLocation, useRouteMatch } from 'react-router-dom';
import urljoin from 'url-join';

import { useStyles } from '~/assets/styles';
import CardLink from '~/components/core/CardLink';
import EmptyState from '~/components/core/EmptyState';
import GenericTemplatesConfigurationScreen from '~/components/GenericTemplates/GenericTemplatesConfiguration/GenericTemplatesConfigurationScreen';
import StatReserveConfigurationPage from '~/components/SystemConfiguration/ClaimConfiguration/StatReserveConfiguration/StatReserveConfigurationPage';
import PredefinedPromptsTab from '~/components/SystemConfiguration/CliveConfiguration/PredefinedPromptsTab';
import ContactRolesConfiguration from '~/components/SystemConfiguration/ContactRoles';
import DelegatedAuthorityTab from '~/components/SystemConfiguration/DelegatedAuthority/DelegatedAuthorityTab';
import FnolConfigScreen from '~/components/SystemConfiguration/FnolConfiguration';
import GlobalConfiguration from '~/components/SystemConfiguration/GlobalConfiguration';
import NotificationsConfiguration from '~/components/SystemConfiguration/NotificationsConfiguration';
import { OPERATIONS_MIXPANEL_EVENTS } from '~/pocs/mixpanel';
import { ADMIN_ROUTES } from '~/routesConstants';
import { isUserFiveSigma } from '~/UserUtils';
import cn from '~/Utils/cn';

import CardDialog from '../CardDialog';
import mixpanel from '../CmsMain/mixpanel';
import { PERMISSION_ACTIONS, PERMISSION_VERBS } from '../core/Permissions/PermissionUtils';
import { useCms } from '../hooks/useCms';
import { useHasMultiplePermissions } from '../hooks/useHasPermission';

import ClaimConfigurationPage, { CLAIM_CONFIG_CONFIGURATION_SUBTITLE } from './ClaimConfiguration/ClaimConfiguration';
import CommunicationConfigurations from './Communications/CommunicationConfigurations';
import ConfigurationManagement from './ConfigurationManagement/ConfigurationManagement';
import DataReportingPage from './DataExport/DataReportingPage';
import ExternalAdminCcc from './Integrations/CCC/ExternalAdmin';
import ExternalAdminClaimSearch from './Integrations/ClaimSearch/ExternalAdmin';
import ExternalAdminNavigatorCard from './Integrations/Navigator/ExternalAdminNavigatorWrapper';
import TableauAccountsExternalAdmin from './TableauAccounts/TableauAccountsExternalAdmin';
import DeveloperToolsTab from './Tabs/DeveloperTools/DeveloperToolsTab';
import PerformanceQATab from './Tabs/PerformanceQA';
import { PermissionsProfilesPage } from './Tabs/PermissionsProfiles/PermissionsProfilesPage';
import SupportedLocations from './Tabs/SupportedLocations';
import AutomaticRulesConfiguration, { AUTOMATIC_RULES_CONFIGURATION_SUBTITLE } from './AutomaticRulesConfiguration';
import CatEventsTable from './CatastropheEvents';
import LoginConfiguration from './LoginConfiguration';
import PolicyConfiguration from './PolicyConfiguration';
import SubOrganizations from './SubOrganizations';
import { SURFACE_ORG_AVAILABILITY } from './sysconfigScopeAvailability';
import { useSysconfig } from './SystemConfigurationScreen';
import SystemOfRecord, { SYSTEM_OF_RECORD_SUBTITLE } from './SystemOfRecord';

const LazyEmptyConfigurationIllustration = lazy(
  () => import('~/components/illustrations/EmptyConfigurationIllustration')
);

// Organization-level tile availability (`isEnabled`) comes from the shared
// predicates in sysconfigScopeAvailability, which also drive the matching scope
// rows on the permission profile screen — keep the two in sync by construction.
// Per-user conditions come in two flavors, both gating only the tile:
// `isEnabledForUser` restricts it (must hold the permission AND match), while
// `orIsVisibleForUser` widens it (the permission OR the predicate suffices).
// `orReadPermissionAction` widens it the same way, for a second action held at READ.
export const OPERATIONS_CARDS = [
  {
    title: 'Performance QA Builder',
    // Org-level supervisors get the builder out of the box (master's behavior);
    // the granular scope lets a profile grant it to anyone else.
    permissionAction: PERMISSION_ACTIONS.SYSCONFIG_PERFORMANCE_QA,
    text: 'Enable to create QA questionnaires using the QA Form Wizard.',
    isEnabled: SURFACE_ORG_AVAILABILITY.always,
    orIsVisibleForUser: (user) => user.is_org_level_supervisor,
    url: 'performance_qa',
    Component: PerformanceQATab,
  },
  {
    title: 'Sub Organizations',
    permissionAction: PERMISSION_ACTIONS.SYSCONFIG_SUB_ORGANIZATIONS,
    text: 'Enable classifying claims per partner and/or MGA and manage different configurations accordingly.',
    isEnabled: SURFACE_ORG_AVAILABILITY.subOrganizations,
    url: 'sub_organizations',
    Component: SubOrganizations,
  },
  {
    title: 'Login Configuration',
    permissionAction: PERMISSION_ACTIONS.SYSCONFIG_LOGIN,
    text: 'Manage login configuration and authentication methods.',
    isEnabled: SURFACE_ORG_AVAILABILITY.always,
    url: 'login_configuration',
    Component: LoginConfiguration,
  },
  {
    title: 'Catastrophic Events',
    permissionAction: PERMISSION_ACTIONS.SYSCONFIG_CAT_EVENTS,
    text: 'Add and manage catastrophic events for the ability to mark claims as catastrophe.',
    isEnabled: SURFACE_ORG_AVAILABILITY.catEvents,
    url: 'cat_events',
    Component: CatEventsTable,
  },
  {
    title: 'Automatic Rules Configuration',
    permissionAction: PERMISSION_ACTIONS.SYSCONFIG_AUTOMATION,
    text: AUTOMATIC_RULES_CONFIGURATION_SUBTITLE,
    isEnabled: SURFACE_ORG_AVAILABILITY.always,
    url: 'automation',
    Component: AutomaticRulesConfiguration,
  },
  {
    title: 'Data & Reporting',
    permissionAction: PERMISSION_ACTIONS.SYSCONFIG_DATA_REPORTING,
    text: 'Create data exports and manage reports.',
    isEnabled: SURFACE_ORG_AVAILABILITY.dataReporting,
    url: 'data_reporting',
    Component: DataReportingPage,
  },
  {
    title: 'Navigator',
    permissionAction: PERMISSION_ACTIONS.SYSCONFIG_INTEGRATIONS,
    text: 'Enable integration with Navigator',
    isEnabled: SURFACE_ORG_AVAILABILITY.navigator,
    url: 'navigator',
    Component: ExternalAdminNavigatorCard,
  },
  {
    title: 'ClaimSearch',
    permissionAction: PERMISSION_ACTIONS.SYSCONFIG_INTEGRATIONS,
    text: 'Enable integration with ClaimSearch\n(Access cause of loss codes setup from here)',
    isEnabled: SURFACE_ORG_AVAILABILITY.claimSearch,
    url: 'claim_search',
    Component: ExternalAdminClaimSearch,
  },
  {
    title: 'Supported Locations',
    permissionAction: PERMISSION_ACTIONS.SYSCONFIG_LOCATIONS,
    text: 'Configure the supported locations per each location category.',
    isEnabled: SURFACE_ORG_AVAILABILITY.always,
    url: 'supported_locations',
    Component: () => <SupportedLocations displayBreadcrumbs displayTitle />,
  },
  {
    title: 'Communication',
    permissionAction: PERMISSION_ACTIONS.SYSCONFIG_COMMUNICATIONS,
    text: 'Manage communication configurations and settings. Includes general email classification.',
    isEnabled: SURFACE_ORG_AVAILABILITY.always,
    url: 'communications',
    Component: CommunicationConfigurations,
    disablePaddingToComponent: true,
  },
  {
    title: PermissionsProfilesPage.title,
    permissionAction: PERMISSION_ACTIONS.SYSCONFIG_PERMISSIONS,
    text: PermissionsProfilesPage.subtitle,
    isEnabled: SURFACE_ORG_AVAILABILITY.permissionsProfiles,
    url: 'permissions',
    Component: PermissionsProfilesPage,
    disablePaddingToComponent: true,
  },
  {
    title: 'Dev Tools',
    // No granular scope of its own: Dev Tools is a Five Sigma surface (its routes refuse
    // customer-org callers), so the tile takes the parent entry permission and is shown
    // only to Five Sigma users — a customer entrant would otherwise reach tabs that 403.
    permissionAction: PERMISSION_ACTIONS.SYSTEM_CONFIGURATION,
    text: 'dev tools text',
    isEnabled: SURFACE_ORG_AVAILABILITY.devTools,
    isEnabledForUser: (user) => isUserFiveSigma(user),
    url: 'dev_tools',
    Component: DeveloperToolsTab,
  },
  {
    title: 'Claim Configuration',
    permissionAction: PERMISSION_ACTIONS.SYSCONFIG_CLAIM_CONFIG,
    text: CLAIM_CONFIG_CONFIGURATION_SUBTITLE,
    isEnabled: SURFACE_ORG_AVAILABILITY.always,
    url: 'claim_config',
    Component: ClaimConfigurationPage,
  },
  {
    title: 'FNOL Configuration',
    permissionAction: PERMISSION_ACTIONS.SYSCONFIG_POLICY_FNOL,
    text: 'Customize elements on the FNOL form. You can configure a custom FNOL form for different sub organizations and lines of business.',
    isEnabled: SURFACE_ORG_AVAILABILITY.policyAndFnolConfiguration,
    url: 'fnol_config',
    Component: FnolConfigScreen,
    disablePaddingToComponent: true,
  },
  {
    title: 'Global Configuration',
    permissionAction: PERMISSION_ACTIONS.SYSCONFIG_GLOBAL_FIELDS,
    text: 'Define policy and FNOL fields once for the whole organization. They appear on every configuration in their context and cannot be edited there.',
    isEnabled: SURFACE_ORG_AVAILABILITY.globalConfiguredFields,
    url: ADMIN_ROUTES.GLOBAL_CONFIGURATION.MAIN,
    Component: GlobalConfiguration,
    disablePaddingToComponent: true,
  },
  {
    title: 'Policy Configuration',
    permissionAction: PERMISSION_ACTIONS.SYSCONFIG_POLICY_FNOL,
    text: 'Customize elements on the policy form. You can configure a custom policy form for different sub organizations and lines of business.',
    isEnabled: SURFACE_ORG_AVAILABILITY.policyAndFnolConfiguration,
    url: 'policy_configuration',
    Component: PolicyConfiguration,
    disablePaddingToComponent: true,
  },
  {
    title: 'Tableau Accounts Manager',
    permissionAction: PERMISSION_ACTIONS.SYSCONFIG_DATA_REPORTING,
    text: 'Manage Tableau account access',
    isEnabled: SURFACE_ORG_AVAILABILITY.tableauAccounts,
    url: 'tableau_accounts',
    Component: TableauAccountsExternalAdmin,
  },
  {
    title: 'CCC',
    permissionAction: PERMISSION_ACTIONS.SYSCONFIG_INTEGRATIONS,
    text: 'Manage CCC integration',
    isEnabled: SURFACE_ORG_AVAILABILITY.ccc,
    url: 'ccc',
    Component: ExternalAdminCcc,
  },
  {
    title: 'Templates',
    permissionAction: PERMISSION_ACTIONS.SYSCONFIG_TEMPLATES,
    text: 'Create, edit and preview the templates for Emails, Notes and SMS.',
    isEnabled: SURFACE_ORG_AVAILABILITY.always,
    url: 'generic_templates_configuration',
    Component: GenericTemplatesConfigurationScreen,
  },
  {
    title: 'Contact Roles',
    permissionAction: PERMISSION_ACTIONS.SYSCONFIG_CONTACT,
    text: 'View and manage contact roles in the system. Includes setting up and defining custom contact roles.',
    isEnabled: SURFACE_ORG_AVAILABILITY.always,
    url: 'contact_roles',
    Component: ContactRolesConfiguration,
  },
  {
    title: 'Reserve Configuration',
    permissionAction: PERMISSION_ACTIONS.SYSCONFIG_RESERVES,
    text: 'Configure custom rules to automatically set initial reserves when opening a new exposure.',
    isEnabled: SURFACE_ORG_AVAILABILITY.always,
    url: ADMIN_ROUTES.STAT_RESERVES,
    Component: StatReserveConfigurationPage,
  },
  {
    title: 'Notifications Configuration',
    permissionAction: PERMISSION_ACTIONS.SYSCONFIG_NOTIFICATIONS,
    text: 'Configure categories for notifications',
    isEnabled: SURFACE_ORG_AVAILABILITY.always,
    url: ADMIN_ROUTES.NOTIFICATIONS_CONFIGURATION.MAIN,
    Component: NotificationsConfiguration,
  },
  {
    title: 'Configuration Management',
    permissionAction: PERMISSION_ACTIONS.SYSCONFIG_CONFIG_MANAGEMENT,
    text: 'Manage snapshots, promotions, audit log and change requests',
    isEnabled: SURFACE_ORG_AVAILABILITY.configurationManagement,
    url: 'configuration_management',
    Component: ConfigurationManagement,
  },
  {
    title: 'Delegated Authority',
    permissionAction: PERMISSION_ACTIONS.SYSCONFIG_DELEGATED_AUTHORITY,
    text: 'Customize Authority levels and Override Rules.',
    isEnabled: SURFACE_ORG_AVAILABILITY.delegatedAuthority,
    url: ADMIN_ROUTES.DELEGATED_AUTHORITY,
    Component: DelegatedAuthorityTab,
  },
  {
    title: 'Chat Suggestions',
    permissionAction: PERMISSION_ACTIONS.SYSCONFIG_CHAT_SUGGESTIONS,
    text: 'Manage the predefined prompts offered to users in the Clive chat.',
    isEnabled: SURFACE_ORG_AVAILABILITY.always,
    url: ADMIN_ROUTES.CLIVE_CONFIGURATION.PREDEFINED_PROMPTS,
    Component: PredefinedPromptsTab,
  },
  {
    title: 'System of Record',
    permissionAction: PERMISSION_ACTIONS.SYSCONFIG_SYSTEM_OF_RECORD,
    // Its Issues tab is for operators the configuration is not open to.
    orReadPermissionAction: PERMISSION_ACTIONS.SOR_ISSUES_MANAGE,
    text: SYSTEM_OF_RECORD_SUBTITLE,
    isEnabled: SURFACE_ORG_AVAILABILITY.systemOfRecord,
    url: 'system-of-record',
    Component: SystemOfRecord,
  },
];

const OperationsTab = () => {
  const { organization, organizationOperationalDetails } = useSysconfig();
  const { user } = useCms();
  const match = useRouteMatch();
  const { classes } = useStyles();
  const location = useLocation();

  const shouldDisplayBreadcrumbs = () => {
    const path = location.pathname.substring(match.url.length + 1);
    const breadcrumbs = path.split('/');

    return breadcrumbs.length === 1 && !breadcrumbs[0];
  };

  const cardPermissions = useHasMultiplePermissions({
    actions: OPERATIONS_CARDS.map((card) => card.permissionAction),
    verb: PERMISSION_VERBS.FULL,
  });
  // `orIsVisibleForUser` widens the permission gate: the card also shows for users
  // matching the predicate even without the action (e.g. org-level supervisors get
  // the Performance QA Builder out of the box).
  const cardReadPermissions = useHasMultiplePermissions({
    actions: OPERATIONS_CARDS.filter((card) => card.orReadPermissionAction).map((card) => card.orReadPermissionAction),
    verb: PERMISSION_VERBS.READ,
  });
  const permittedCards = OPERATIONS_CARDS.filter(
    (card) =>
      cardPermissions[card.permissionAction] ||
      cardReadPermissions[card.orReadPermissionAction] ||
      card.orIsVisibleForUser?.(user)
  );

  const path = location.pathname.substring(match.url.length + 1);
  const breadcrumbs = path.split('/');
  const currCardIndex = permittedCards.findIndex((card) => breadcrumbs.includes(card.url));
  const cardTitle = currCardIndex >= 0 ? permittedCards[currCardIndex].title : '';

  useEffect(() => {
    if (currCardIndex < 0) {
      mixpanel.track(OPERATIONS_MIXPANEL_EVENTS.OPERATIONS_SCREEN_VIEWED);
    } else {
      mixpanel.track(OPERATIONS_MIXPANEL_EVENTS.OPERATIONS_COMPONENT_CLICKED, {
        component: cardTitle,
      });
    }
  }, [cardTitle, currCardIndex]);

  const filteredCard = permittedCards.filter(
    ({ isEnabled, isEnabledForUser }) =>
      isEnabled({ organization, organizationOperationalDetails }) && (!isEnabledForUser || isEnabledForUser(user))
  );
  filteredCard.sort((a, b) => (a.title > b.title ? 1 : -1));

  return (
    <CardDialog noCardTitle contentStyle={{ padding: '0px' }}>
      {shouldDisplayBreadcrumbs() &&
        (filteredCard.length > 0 ? (
          <div className="ml-16 mt-24 flex flex-wrap pl-32 pt-16">
            {filteredCard.map(({ text, title, url }) => (
              <CardLink key={title} title={title} text={text} to={`${match.url}/${url}`} />
            ))}
          </div>
        ) : (
          <div className="py-64 flex w-full items-center justify-center">
            <EmptyState
              title="No permissions granted"
              subtitle="Your profile has no operations permissions. Please refer to your system administrator to add them to your permission profile."
              illustration={
                <Suspense fallback={<div className="h-[235px]" />}>
                  <LazyEmptyConfigurationIllustration />
                </Suspense>
              }
            />
          </div>
        ))}
      <div className={classes.pageBody}>
        <Switch>
          <Redirect exact from={`${match.path}/`} to={urljoin(match.path, '')} />
          {/* Routes are direct Switch children so the trailing catch-all is reachable: a
              fragment carries no `path`, and Switch treats a pathless child as a match,
              which would swallow every URL a permitted card does not claim. */}
          {permittedCards.map(({ url, Component, disablePaddingToComponent = false }) => (
            <Route
              key={url}
              path={urljoin(match.path, url)}
              render={(routerProps) => (
                <div className={cn({ 'pb-24 pl-16 pr-24 pt-4': !disablePaddingToComponent })}>
                  <Component classes={classes} {...routerProps} />
                </div>
              )}
            />
          ))}
          <Redirect to={match.path} />
        </Switch>
      </div>
    </CardDialog>
  );
};

export default OperationsTab;
